import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Building2, Phone, MapPin, Search, ShieldCheck, HeartHandshake } from "lucide-react";

const Clinics = () => {
  const [params, setParams] = useSearchParams();
  const [clinics, setClinics] = useState([]);
  const [q, setQ] = useState("");
  // Iter 68.66.i — `?fonte=raf|fp|todos` bookmarkeável, paridade com
  // `?tipo=X` da vitrine FP. Fallback pra "todos" quando valor inválido.
  const initialSource = ["raf", "fp", "todos"].includes(params.get("fonte"))
    ? params.get("fonte") : "todos";
  const [source, setSource] = useState(initialSource);

  useEffect(() => { api.get("/clinics").then(({ data }) => setClinics(data)); }, []);

  const setSourceAndUrl = (next) => {
    setSource(next);
    const url = new URLSearchParams(params);
    if (next === "todos") url.delete("fonte");
    else url.set("fonte", next);
    setParams(url, { replace: false });
  };

  const counts = { todos: clinics.length, raf: 0, fp: 0 };
  clinics.forEach((c) => { if (c.source === "fp") counts.fp++; else counts.raf++; });

  const filtered = clinics.filter((c) => {
    // Filtro origem (paridade com PublicProfessionals "Só RAF / Só FP")
    if (source === "raf" && c.source === "fp") return false;
    if (source === "fp" && c.source !== "fp") return false;
    const s = q.toLowerCase();
    if (!s) return true;
    const hay = `${c.name || ""} ${c.city || ""} ${(c.substances || []).join(" ")} ${c.establishment_type || ""}`.toLowerCase();
    return hay.includes(s);
  });

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Rede de clínicas RAF</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-1">Encontre a clínica ideal</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Explore a rede de clínicas parceiras. Após a triagem, o consultor RAF apresenta as opções mais compatíveis com o perfil do paciente. Iter 68.66.f — a rede agora inclui também clínicas do Filho Presente marcadas como parceiras da RAF.
        </p>
      </div>

      <div className="relative mb-4">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <input data-testid="clinic-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por nome, cidade, substância ou tipo"
          className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-md outline-none focus:ring-2 focus:ring-[#0B2B62]" />
      </div>

      {/* Iter 68.66.h — Filtro por origem (paridade com vitrine de profissionais) */}
      <div className="flex flex-wrap gap-2 mb-6" role="tablist" aria-label="Filtro por origem">
        {[
          { k: "todos", label: `Todos (${counts.todos})` },
          { k: "raf",   label: `Só RAF Direct (${counts.raf})` },
          { k: "fp",    label: `Só Filho Presente (${counts.fp})` },
        ].map(({ k, label }) => (
          <button key={k} type="button" onClick={() => setSourceAndUrl(k)} role="tab"
            aria-selected={source === k}
            data-testid={`clinic-source-${k}`}
            className={`text-xs uppercase font-bold tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
              source === k
                ? (k === "fp"
                    ? "bg-amber-500 border-amber-500 text-white"
                    : "bg-[#0B2B62] border-[#0B2B62] text-white")
                : "bg-white border-slate-300 text-slate-600 hover:border-[#0B2B62]"
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`clinic-${c.id}`} className="relative bg-white border border-slate-200 rounded-xl p-6 hover:border-[#0B2B62] transition-colors">
            {c.source === "fp" && (
              <span
                title="Parceiro do Filho Presente — habilitado também para famílias RAF Direct."
                data-testid={`clinic-fp-badge-${c.id}`}
                className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded bg-amber-500 text-white">
                <HeartHandshake className="w-2.5 h-2.5" /> Filho Presente
              </span>
            )}
            {c.source !== "fp" && (
              <span
                title="Clínica RAF Direct — rede original."
                className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded bg-emerald-600 text-white">
                <ShieldCheck className="w-2.5 h-2.5" /> RAF
              </span>
            )}
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-lg bg-[#0B2B62] text-white flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div className="min-w-0 flex-1 pr-16">
                <h3 className="font-display font-bold text-xl text-[#0B2B62] tracking-tight">{c.name}</h3>
                <div className="text-sm text-slate-600 flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3.5 h-3.5" /> {c.city}{c.state && `, ${c.state}`}
                </div>
                {c.establishment_type && (
                  <div className="text-xs text-slate-500 mt-0.5 italic">{c.establishment_type}</div>
                )}
                {c.description && <p className="text-sm text-slate-600 mt-3">{c.description}</p>}
                {(c.substances || []).length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {c.substances.map((s) => <span key={s} className="text-xs px-2 py-0.5 bg-slate-100 border border-slate-200 rounded-full text-slate-700">{s}</span>)}
                  </div>
                )}
                {c.source !== "fp" && (
                  <div className="mt-4 grid grid-cols-2 gap-3 text-xs text-slate-600">
                    <div><span className="uppercase tracking-widest text-slate-500">Modalidade</span><div className="mt-0.5 capitalize">{(c.modality || []).join(", ") || "—"}</div></div>
                    <div><span className="uppercase tracking-widest text-slate-500">Vagas</span><div className="mt-0.5 font-mono">{c.beds_available ?? "—"}</div></div>
                  </div>
                )}
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
                  {c.phone ? (
                    <a href={`tel:${c.phone}`} className="text-sm text-[#0B2B62] flex items-center gap-1.5 hover:underline"><Phone className="w-3.5 h-3.5" /> {c.phone}</a>
                  ) : (
                    <span className="text-xs text-slate-400">Contato via consultor</span>
                  )}
                  {c.source !== "fp" ? (
                    <span className="text-xs text-slate-500">Faixa: {c.age_range?.[0] ?? 0}-{c.age_range?.[1] ?? 200} anos</span>
                  ) : (
                    <Link to={`/filho-presente/clinicas/${c.id}`} data-testid={`clinic-fp-open-${c.id}`}
                      className="text-xs text-amber-700 hover:underline font-semibold">
                      Ver ficha FP →
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Clinics;
